import { IFutureOrder } from '../../../sp-types/FutureOrderType';
import { DEFAULT_COLOR } from '../../../sp-types/InventoryType';
import { StackedBarChartElement } from '../../../sp-types/dashboardTypes';
import { Material } from '../../../sp-types/materialType';
import { months, MonthYear } from '../../../utils/MonthYear/monthYear';
import { getDate, getMonthYear } from '../../FinancialPositions/components/FinancialPositionsUtils';
import { materialExposureDictionary } from '../FutureOrders';

/* eslint-disable */

export const aggregateFutureOrdersToMaterialsDic = (
  futureOrders: IFutureOrder[],
  selMaterial?: string,
  materialPriceDic?: Record<string, number>,
  isAccumulate: boolean = false,
  isQuant: boolean = false,
): Record<string, materialExposureDictionary> => {
  const materialDictionary: Record<string, materialExposureDictionary> = {};
  const now = getMonthYear(new Date());
  futureOrders.forEach((order) => {
    const exposures = isAccumulate
      ? order.totalExposure
      : Object.entries(order.monthExposure || {})?.map(([formulaId, exposureCalendar]) => ({
          formulaId,
          exposureCalendar,
        }));
    exposures?.forEach(({ formulaId, exposureCalendar }) => {
      if (!selMaterial || selMaterial === formulaId) {
        // Initialize if doesn't exist
        if (!materialDictionary[formulaId]) {
          materialDictionary[formulaId] = {
            exposureCalendar: {},
            totalValue: 0,
            totalWeight: 0,
          };
        }
        
        // Aggregate exposure calendar values
        exposureCalendar
          .filter(({ date }) => (date.month >= now.month && date.year === now.year) || date.year > now.year)
          .forEach(({ date, value }) => {
            const updatedValue =
              materialPriceDic?.[formulaId] && !isQuant ? value * materialPriceDic?.[formulaId] : value;
            const dateString = date.year.toString() + date.month.toString();
            materialDictionary[formulaId].exposureCalendar[dateString] = 
              (materialDictionary[formulaId].exposureCalendar[dateString] || 0) + Number(updatedValue);
            materialDictionary[formulaId].totalValue = 
              Number(materialDictionary[formulaId].totalValue) + Number(updatedValue);
          });
      }
    });
  });

  return materialDictionary;
};

export const getMonthYearAfter = (date: Date, addMonths: number): MonthYear => {
  const nextDate = getDate(date, addMonths);
  return { month: nextDate.getMonth() + 1, year: nextDate.getFullYear() };
};

export const dictionaryToStackChart = (
  materialMap: Record<string, materialExposureDictionary>,
  colors: Record<string, string>,
  materialsMap: Record<string, Material>,
  year: number,
  pricesMap: Record<string, number>,
  selectedMaterial?: Material | null,
): StackedBarChartElement[] => {
  const now = new Date();
  now.setFullYear(year);

  if (selectedMaterial) {
    const yearMonth = `${year}${now.getMonth() + 1}`;

    const currMonthVal = Object.entries(materialMap)
      .filter(([materialId, _]) => materialId !== selectedMaterial.id)
      .reduce((acc, [_, value]) => acc + (value.exposureCalendar[yearMonth] || 0), 0);
    materialMap['placeholder'] = {
      totalValue: currMonthVal,
      totalWeight: 0,
      price: pricesMap[selectedMaterial.id] || 0,
      exposureCalendar: { [yearMonth]: currMonthVal },
    };
  }

  return Object.entries(materialMap)
    .filter(([materialId]) => !selectedMaterial || ['placeholder', selectedMaterial.id].includes(materialId))
    .map(
      ([materialId, { totalValue, exposureCalendar }]): StackedBarChartElement => ({
        formulaId: materialId,
        fieldName: materialsMap[materialId]?.name || materialId,
        color: colors[materialId] || DEFAULT_COLOR,
        valueArray: months
          .map((_, monthIndex) => getMonthYearAfter(now, monthIndex))
          .map(({ month, year }) => ({
            value: exposureCalendar[`${year}${month}`] || 0,
            date: { month, year },
          })),
        sumValue: Number(totalValue) || 0,
        totalWeight: (Number(totalValue) || 0) / (pricesMap[materialId] || 1),
        unit: materialsMap[materialId]?.unit,
        price: pricesMap[materialId] || 0,
      }),
    );
};

export const getFutureOrderExposedMaterials = (allFutureOrders: IFutureOrder[]): string[] => {
  return Array.from(
    new Set<string>(allFutureOrders.flatMap((order) => (order.monthExposure ? Object.keys(order.monthExposure) : []))),
  );
};
